import { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import { Row, Col, Button, Card, Form as BsForm } from "react-bootstrap";
import * as Yup from "yup";

import { VzPageContainer } from "../../layouts/VzPageContainer";
import FormikControl from "../../layouts/Input/FormikControl";
import { AllowOnlyNumeric } from "../../utilities/Validations";
import axios from "../../apis/Api";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {  useRef } from "react";
/* ================= INITIAL VALUES ================= */

const initialValues = {
  Id: null,
  distributorId: "",
  distributorName: "",
  ntn: "",
  secp: "",
  strn: "",
  phoneNumber: "",
  businessAddress: "",
  reason: "",
  onboardingStatus: 1,

  bankName: "",
  branchCode: "",
  anchorBankCode: "",
  accountNumber: "",
  accountTitle: "",

  contactEmployeeName: "",
  designation: "",
  contactEmployeeDetails: "",
};

/* ================= VALIDATION ================= */

const validationSchema = Yup.object({
  distributorId: Yup.string().required("Distributor is required"),
  ntn: Yup.string().required("NTN is required"),
  phoneNumber: Yup.string().required("Phone number is required"),
  accountNumber: Yup.string().required("Account number is required"),
  accountTitle: Yup.string().required("Account title is required"),
});

/* ================= STATUS OPTIONS ================= */

const DISTRIBUTOR_ONBOARDING_STATUS_OPTIONS = [
  { label: "Pending", value: 1 },
  { label: "In Review", value: 2 },
  { label: "Approved", value: 3 },
  { label: "Rejected", value: 4 },
];

const DistributorOnboardingForm = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const isView = location.pathname.includes("/view/");
  const isEdit = location.pathname.includes("/edit/");
  const readOnly = isView;

  const [recordData, setRecordData] = useState(null);
  const [distributorOptions, setDistributorOptions] = useState([]);
  const formikRef = useRef(null); // ✅ MUST be here

  /* ================= LOAD DISTRIBUTORS DDL ================= */

  useEffect(() => {
    loadDistributorsDDL();
  }, []);

  const loadDistributorsDDL = async () => {
    //debugger;
    try {
      const { data } = await axios.get("api/Distributor/GetDistributorsDDL");
      if (data?.response) {
        setDistributorOptions(data.response);
      }
    } catch {
      toast.error("Failed to load distributors");
    }
  };

  /* ================= LOAD RECORD (EDIT / VIEW) ================= */

  useEffect(() => {
    if (id)
      fetchDistributorDetails(id);
  }, [id]);

  /* ================= FETCH DISTRIBUTOR DETAILS ================= */

  const fetchDistributorDetails = async (Id) => {
  try {
    dispatch(showLoading());

    const { data } = await axios.get(
      `/api/DistributorOnboarding/GetDistributorOnbaordDetails/${Id}`
    );
//debugger;
   if (data?.isSuccess && data.response && formikRef.current) {
  const d = data.response;

  // Distributor basic info
  formikRef.current.setFieldValue("Id", d.id);
  formikRef.current.setFieldValue("distributorId", Id);
  formikRef.current.setFieldValue("distributorName", d.distributorName || "");
  formikRef.current.setFieldValue("ntn", d.ntn || "");
  formikRef.current.setFieldValue("secp", d.secp || "");
  formikRef.current.setFieldValue("strn", d.strn || "");
  formikRef.current.setFieldValue("phoneNumber", d.phoneNumber || "");
  formikRef.current.setFieldValue(
    "businessAddress",
    d.businessAddress || ""
  );
  formikRef.current.setFieldValue("reason", d.reason || "");
  formikRef.current.setFieldValue(
    "onboardingStatus",
    d.onboardingStatus ?? 1
  );

  // Bank details
  formikRef.current.setFieldValue("bankName", d.bankName || "");
  formikRef.current.setFieldValue("branchCode", d.branchCode || "");
  formikRef.current.setFieldValue("anchorBankCode", d.anchorBankCode || "");
  formikRef.current.setFieldValue("accountNumber", d.accountNumber || "");
  formikRef.current.setFieldValue("accountTitle", d.accountTitle || "");

  // Contact details
  formikRef.current.setFieldValue(
    "contactEmployeeName",
    d.contactEmployeeName || ""
  );
  formikRef.current.setFieldValue("designation", d.designation || "");
  formikRef.current.setFieldValue(
    "contactEmployeeDetails",
    d.contactEmployeeDetails || ""
  );
}
 else if (formikRef.current) {
  // RESET ALL FIELDS IF NOT SUCCESS
  const { distributorId, distributorName } = formikRef.current.values;

  formikRef.current.setValues({
    distributorId,
    distributorName,
    Id: null,
    ntn: "",
    secp: "",
    strn: "",
    phoneNumber: "",
    businessAddress: "",
    reason: "",
    onboardingStatus: 1,

    bankName: "",
    branchCode: "",
    anchorBankCode: "",
    accountNumber: "",
    accountTitle: "",

    contactEmployeeName: "",
    designation: "",
    contactEmployeeDetails: "",
  });
}
  } catch {
    toast.error("Failed to load distributor details.");
  } finally {
    dispatch(hideLoading());
  }
};

  /* ================= SUBMIT ================= */

  const handleSubmit = async (values) => {
    //debugger;
    try {
      const url = isEdit
        ? "api/DistributorOnboarding/PostDistributorRequest"
        : "api/DistributorOnboarding/PostDistributorRequest";

      const { data } = await axios.post(url, values);

      if (data?.isSuccess) {
        toast.success("Distributor onboarding saved successfully");
        history.push("/MBLDistributorOnboardingList");
      } else {
        toast.error(data?.message || "Submission failed");
      }
    } catch {
      toast.error("Error while submitting onboarding");
    }
  };

  /* ================= RENDER ================= */

  return (
    <VzPageContainer title="MBL Distributor Onboarding">
      <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={readOnly ? null : validationSchema}
          onSubmit={handleSubmit}
        >

        {(formik) => (
          <Form autoComplete="off">
            {/* ================= DISTRIBUTOR INFORMATION ================= */}

            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">
                  <b>Distributor Information</b>
                </h5>

                <Row className="mb-3">
                  <Col md={6}>
                    <FormikControl
                      formik={formik}
                      control="react-select"
                      label="Distributor Name"
                      name="distributorId"
                      type="distributorId"
                      options={distributorOptions}
                      value={distributorOptions.find(
                           o => String(o.value).toLocaleLowerCase() === String(formik.values.distributorId).toLocaleLowerCase()
                      )}
                      handleOnChange={(option) => {
                        //debugger;
                        formik.setFieldValue("distributorId", option.value);
                        formik.setFieldValue("distributorName", option.label);
                        fetchDistributorDetails(option.value, formik);
                      }}
                      disabled={readOnly || isEdit}
                      isDisabled={readOnly || isEdit}
                    />
                  </Col>

                  <Col md={6}>
                    <FormikControl
                      formik={formik}
                      label="NTN No."
                      control="input"
                      name="ntn"
                      disabled={readOnly}
                      value={formik.values["ntn"] ?? ""}
                      handleOnChange={(v) => {
                        formik.setFieldValue("ntn", v);
                      }}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FormikControl
                      formik={formik}
                      control="input"
                      label="SECP No."
                      name="secp"
                      disabled={readOnly}
                      value={formik.values["secp"] ?? ""}
                      handleOnChange={(v) => {
                        formik.setFieldValue("secp", v);
                      }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormikControl
                      formik={formik}
                      control="input"
                      label="STRN No."
                      name="strn"
                      value={formik.values["strn"] ?? ""}
                      disabled={readOnly}
                       handleOnChange={(v) => {
                        formik.setFieldValue("strn", v);
                      }}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <FormikControl
                      formik={formik}
                      control="input"
                      label="Phone No."
                      name="phoneNumber"
                      disabled={readOnly}
                      value={formik.values["phoneNumber"] ?? ""}
                     handleOnChange={(v) => {
                        formik.setFieldValue("phoneNumber", v);
                      }}
                    />
                  </Col>

                  <Col md={6}>
                    <FormikControl
                      formik={formik}
                      control="react-select"
                      label="Distributor Status"
                      name="onboardingStatus"
                      options={DISTRIBUTOR_ONBOARDING_STATUS_OPTIONS}
                      value={DISTRIBUTOR_ONBOARDING_STATUS_OPTIONS.find(
                        (x) => x.value === formik.values.onboardingStatus,
                      )}
                      handleOnChange={(opt) =>
                        formik.setFieldValue("onboardingStatus", opt.value)
                      }
                      disabled={readOnly}
                      isDisabled={readOnly}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <FormikControl
                      formik={formik}
                      control="textarea"
                      label="Business Address"
                      name="businessAddress"
                      disabled={readOnly}
                      value={formik.values["businessAddress"] ?? ""}
                      handleOnChange={(v) => {
                        formik.setFieldValue("businessAddress", v);
                      }}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormikControl
                      formik={formik}
                      control="textarea"
                      label="Reason"
                      name="reason"
                      value={formik.values["reason"] ?? ""}
                      disabled={readOnly}
                        handleOnChange={(v) => {
                        formik.setFieldValue("reason", v);
                      }}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* ================= BANK + CONTACT ================= */}

            <Row>
              <Col md={6}>
                <Card className="mb-4 h-100">
                  <Card.Body>
                    <h5 className="mb-3">
                      <b>Bank Details</b>
                    </h5>

                    <FormikControl
                      formik={formik}
                      control="input"
                      label="Bank Name"
                      name="bankName"
                      disabled={readOnly}
                      value={formik.values["bankName"] ?? ""}
                      handleOnChange={(v) => {
                        formik.setFieldValue("bankName", v);
                      }}
                    />

                    <Row className="mt-3">
                      <Col md={6}>
                        <FormikControl
                          formik={formik}
                          control="input"
                          label="Branch Code"
                          name="branchCode"
                          value={formik.values["branchCode"] ?? ""}
                          disabled={readOnly}
                           handleOnChange={(v) => {
                        formik.setFieldValue("branchCode", v);
                      }}
                        />
                      </Col>
                      <Col md={6}>
                        <FormikControl
                          formik={formik}
                          control="input"
                          label="Anchor Bank Code"
                          name="anchorBankCode"
                          disabled={readOnly}
                          value={formik.values["anchorBankCode"] ?? ""}
                          handleOnChange={(v) => {
                        formik.setFieldValue("anchorBankCode", v);
                      }}
                        />
                      </Col>
                    </Row>

                    <Row className="mt-3">
                      <Col md={6}>
                        <FormikControl
                          formik={formik}
                          control="input"
                          label="Account Number"
                          name="accountNumber"
                          value={formik.values["accountNumber"] ?? ""}
                          disabled={readOnly}
                          handleOnChange={(v) => {
                        formik.setFieldValue("accountNumber", v);
                      }}
                        />
                      </Col>
                      <Col md={6}>
                        <FormikControl
                          formik={formik}
                          control="input"
                          label="Account Title"
                          name="accountTitle"
                          disabled={readOnly}
                          value={formik.values["accountTitle"] ?? ""}
                          handleOnChange={(v) => {
                        formik.setFieldValue("accountTitle", v);
                      }}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-4 h-100">
                  <Card.Body>
                    <h5 className="mb-3">
                      <b>Contact Details</b>
                    </h5>

                    <FormikControl
                      formik={formik}
                      control="input"
                      label="Employee Name"
                      name="contactEmployeeName"
                      disabled={readOnly}
                      value={formik.values["contactEmployeeName"] ?? ""}
                        handleOnChange={(v) => {
                        formik.setFieldValue("contactEmployeeName", v);
                      }}
                    />

                    <Row className="mt-3">
                      <Col md={6}>
                        <FormikControl
                          formik={formik}
                          control="input"
                          label="Designation"
                          name="designation"
                          disabled={readOnly}
                          value={formik.values["designation"] ?? ""}
                        handleOnChange={(v) => {
                        formik.setFieldValue("designation", v);
                      }}
                        />
                      </Col>
                      <Col md={6}>
                        <FormikControl
                          formik={formik}
                          control="input"
                          label="Contact Details"
                          name="contactEmployeeDetails"
                          disabled={readOnly}
                          value={formik.values["contactEmployeeDetails"] ?? ""}
                           handleOnChange={(v) => {
                        formik.setFieldValue("contactEmployeeDetails", v);
                      }}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ================= ACTIONS ================= */}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="outline-secondary"
                onClick={() => history.push("/MBLDistributorOnboardingList")}
              >
                Cancel
              </Button>
              &nbsp;
              {!readOnly && (
                <Button type="submit" variant="primary">
                  Submit Request
                </Button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </VzPageContainer>
  );
};

export default DistributorOnboardingForm;
